import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Palette, RefreshCw, Filter, Send, AlertTriangle, Download, Upload } from "lucide-react";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pantone Reconciler Tile
 *
 * Worksheet for the ~285 (in prod) ACTIVE INP/INM rows whose
 * `cdata.colour` is an ambiguous name (Blue → 9 pantones, Black → 6,
 * Natural → no master row). Designer picks the canonical pantone per
 * row from the candidates surfaced by the backend, applies in batch.
 *
 * Collisions detected by the backend (stamping would land on an
 * existing identity tuple) are surfaced as a separate panel — operator
 * routes those via the existing collision-pair resolver. Reconciler
 * NEVER auto-merges.
 */
export default function PantoneReconcilerTile() {
  const token = useAuthStore((s) => s.token);
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const [loading, setLoading] = useState(false);
  const [queue, setQueue] = useState({ total: 0, rows: [] });
  const [colourFilter, setColourFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");
  const [picks, setPicks] = useState({}); // { rm_id: pantone_code }
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [skip, setSkip] = useState(0);
  const PAGE_SIZE = 100;
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(skip) });
      if (colourFilter.trim()) params.set("colour_filter", colourFilter.trim());
      if (modelFilter.trim()) params.set("model_filter", modelFilter.trim());
      const res = await axios.get(
        `${API}/raw-materials/admin/pantone-reconciler/queue?${params.toString()}`,
        { headers: authHeaders },
      );
      setQueue(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconciler queue failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip]);

  const setPick = (rm_id, pantone_code) => {
    setPicks((prev) => ({ ...prev, [rm_id]: pantone_code }));
  };

  const stampsForApply = Object.entries(picks)
    .filter(([_, code]) => !!code)
    .map(([rm_id, pantone_code]) => ({ rm_id, pantone_code }));

  const apply = async (dryRun) => {
    if (stampsForApply.length === 0) {
      toast.error("Pick at least one pantone before applying");
      return;
    }
    if (!dryRun) {
      const ok = window.confirm(
        `Stamp ${stampsForApply.length} row(s) with the chosen pantones?\n\n` +
        "Collisions (any pair landing on the same identity tuple) will be " +
        "surfaced and SKIPPED — they need the collision-pair resolver."
      );
      if (!ok) return;
    }
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/raw-materials/admin/pantone-reconciler/apply?dry_run=${dryRun}`,
        { stamps: stampsForApply },
        { headers: authHeaders },
      );
      setLastResult(res.data);
      if (dryRun) {
        toast.success(`Dry-run: ${res.data.safe_to_stamp} ready, ${res.data.collisions?.length || 0} collisions`);
      } else {
        toast.success(`Applied: ${res.data.applied} stamped, ${res.data.collisions?.length || 0} collisions surfaced`);
        // Clear picks for rows that landed
        const applied_ids = new Set((res.data.per_stamp_plan || []).filter(p => p.status === "ready").map(p => p.rm_id));
        setPicks((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !applied_ids.has(k))));
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const progress = `${stampsForApply.length} of ${queue.total} picked`;
  const collisionCount = lastResult?.collisions?.length || 0;

  // Bulk Excel download — honours the active colour/model filters so the
  // designer can scope a worksheet (e.g. "all Blue rows") and edit offline.
  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (colourFilter.trim()) params.set("colour_filter", colourFilter.trim());
      if (modelFilter.trim()) params.set("model_filter", modelFilter.trim());
      const url = `${API}/raw-materials/admin/pantone-reconciler/export${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axios.get(url, { headers: authHeaders, responseType: "blob" });
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      saveAs(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `pantone_reconciler_${ts}.xlsx`);
      toast.success("Worksheet downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  // Bulk Excel upload — re-uses the backend /import which routes through
  // the same collision guard as /apply. Dry-run first so the operator
  // sees safe vs collisions before committing.
  const uploadXlsx = async (file, dryRun) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API}/raw-materials/admin/pantone-reconciler/import?dry_run=${dryRun}`,
        form,
        { headers: { ...authHeaders, "Content-Type": "multipart/form-data" } },
      );
      setLastResult(res.data);
      if (dryRun) {
        toast.success(`Dry-run: ${res.data.safe_to_stamp} ready, ${res.data.collisions?.length || 0} collisions, ${res.data.parsed_rows || 0} parsed`);
      } else {
        toast.success(`Imported: ${res.data.applied} stamped, ${res.data.collisions?.length || 0} collisions`);
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (dryRun) => {
    if (!fileInputRef.current) return;
    fileInputRef.current.dataset.dryRun = dryRun ? "1" : "0";
    fileInputRef.current.value = ""; // allow re-uploading the same file
    fileInputRef.current.click();
  };

  const onFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const dryRun = (fileInputRef.current?.dataset?.dryRun ?? "1") === "1";
    if (!dryRun) {
      const ok = window.confirm(
        `Import "${f.name}" and STAMP every populated pantone_code row?\n\n` +
        "Collisions are detected and skipped (use Dry-run first to preview)."
      );
      if (!ok) return;
    }
    uploadXlsx(f, dryRun);
  };

  return (
    <Card data-testid="pantone-reconciler-tile" className="border-fuchsia-200 bg-fuchsia-50/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-fuchsia-600" />
              Pantone Reconciler
            </CardTitle>
            <CardDescription>
              Stamp canonical `cdata.pantone_code` on the legacy ambiguous-colour INPs.
              Required before strict-mode matcher can be enabled.
            </CardDescription>
          </div>
          <Button data-testid="reconciler-refresh-btn" onClick={load} disabled={loading} variant="outline" size="sm">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* hidden file picker shared by Dry-run upload + Import buttons */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={onFileChange}
          className="hidden"
          data-testid="reconciler-file-input"
        />
        {/* Bulk Excel toolbar */}
        <div className="flex flex-wrap items-center gap-2 border rounded-md bg-white p-2">
          <span className="text-xs uppercase tracking-widest text-slate-500 font-bold">Bulk Excel</span>
          <Button
            data-testid="reconciler-download-btn"
            onClick={downloadXlsx}
            disabled={downloading}
            variant="outline"
            size="sm"
          >
            <Download className={`h-3 w-3 mr-1 ${downloading ? "animate-spin" : ""}`} />
            Download worksheet
          </Button>
          <Button
            data-testid="reconciler-upload-dryrun-btn"
            onClick={() => onPickFile(true)}
            disabled={uploading}
            variant="outline"
            size="sm"
          >
            <Upload className={`h-3 w-3 mr-1 ${uploading ? "animate-spin" : ""}`} />
            Upload (dry-run)
          </Button>
          <Button
            data-testid="reconciler-upload-import-btn"
            onClick={() => onPickFile(false)}
            disabled={uploading}
            size="sm"
            className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          >
            <Upload className={`h-3 w-3 mr-1 ${uploading ? "animate-spin" : ""}`} />
            Upload & stamp
          </Button>
          <span className="text-[10px] text-slate-500 italic">
            Edit the <code>pantone_code</code> column only — collisions are detected on import.
          </span>
        </div>
        {/* Filters + progress */}
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="h-4 w-4 text-slate-500" />
          <Input
            data-testid="reconciler-colour-filter"
            placeholder="Filter colour…" value={colourFilter}
            onChange={(e) => setColourFilter(e.target.value)}
            className="w-44 h-8 text-xs"
          />
          <Input
            data-testid="reconciler-model-filter"
            placeholder="Filter model…" value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            className="w-44 h-8 text-xs"
          />
          <Button data-testid="reconciler-apply-filters-btn" onClick={() => { setSkip(0); load(); }} variant="outline" size="sm">
            Apply
          </Button>
          <div className="flex-1" />
          <Badge variant="outline" className="bg-white">{progress}</Badge>
          <Badge variant="outline" className="bg-white">queue: {queue.total}</Badge>
        </div>

        {/* Worksheet table */}
        <div className="border rounded-md max-h-96 overflow-y-auto bg-white">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left">
                <th className="p-2 w-6"></th>
                <th className="p-2">rm_id</th>
                <th className="p-2">model · part</th>
                <th className="p-2">colour</th>
                <th className="p-2">candidate pantone</th>
                <th className="p-2">used by</th>
              </tr>
            </thead>
            <tbody>
              {queue.rows.map((r) => (
                <tr key={r.rm_id} className="border-t hover:bg-slate-50">
                  <td className="p-2">
                    <Checkbox
                      data-testid={`reconciler-row-${r.rm_id}`}
                      checked={!!picks[r.rm_id]}
                      onCheckedChange={(v) => {
                        if (!v) setPick(r.rm_id, undefined);
                      }}
                    />
                  </td>
                  <td className="p-2 font-mono text-blue-700">{r.rm_id}</td>
                  <td className="p-2 text-slate-700">
                    {r.model_name} · {r.part_name}
                    {r.mould_code && <span className="text-slate-400"> ({r.mould_code})</span>}
                  </td>
                  <td className="p-2"><Badge variant="outline">{r.colour || "—"}</Badge></td>
                  <td className="p-2">
                    {r.candidate_pantones.length === 0 ? (
                      <span className="text-amber-700 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> no master match
                      </span>
                    ) : (
                      <Select
                        value={picks[r.rm_id] || ""}
                        onValueChange={(v) => setPick(r.rm_id, v)}
                      >
                        <SelectTrigger className="h-7 text-xs w-44" data-testid={`reconciler-select-${r.rm_id}`}>
                          <SelectValue placeholder={`${r.candidates_count} candidates…`} />
                        </SelectTrigger>
                        <SelectContent>
                          {r.candidate_pantones.map((p) => (
                            <SelectItem key={p.pantone_code} value={p.pantone_code} data-testid={`reconciler-opt-${r.rm_id}-${p.pantone_code}`}>
                              {p.pantone_code} — {p.pantone_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="p-2 text-[10px] text-slate-500">
                    {r.referencing_bidso_skus.slice(0, 3).join(", ")}
                    {r.referencing_bidso_skus.length > 3 && ` +${r.referencing_bidso_skus.length - 3}`}
                  </td>
                </tr>
              ))}
              {queue.rows.length === 0 && (
                <tr><td colSpan={6} className="p-4 text-center text-slate-500 italic">
                  {loading ? "loading…" : "Queue empty — all rows stamped."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center gap-2 text-xs">
          <Button
            variant="outline" size="sm" disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
            data-testid="reconciler-prev-btn"
          >Prev</Button>
          <span>showing {skip + 1}–{Math.min(skip + queue.rows.length, queue.total)} of {queue.total}</span>
          <Button
            variant="outline" size="sm" disabled={skip + PAGE_SIZE >= queue.total}
            onClick={() => setSkip(skip + PAGE_SIZE)}
            data-testid="reconciler-next-btn"
          >Next</Button>
          <div className="flex-1" />
          <Button onClick={() => apply(true)} disabled={applying || stampsForApply.length === 0} variant="outline" size="sm" data-testid="reconciler-dryrun-btn">
            Dry-run
          </Button>
          <Button onClick={() => apply(false)} disabled={applying || stampsForApply.length === 0} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" size="sm" data-testid="reconciler-apply-btn">
            <Send className={`h-3 w-3 mr-1 ${applying ? "animate-spin" : ""}`} />
            Stamp {stampsForApply.length}
          </Button>
        </div>

        {/* Last result */}
        {lastResult && (
          <div className="border rounded-md bg-white p-3 text-xs">
            <div className="flex items-center gap-2 mb-1">
              <strong>{lastResult.dry_run ? "Last dry-run" : "Last apply"}:</strong>
              <Badge variant="outline">safe: {lastResult.safe_to_stamp}</Badge>
              {collisionCount > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                  collisions: {collisionCount}
                </Badge>
              )}
              {lastResult.applied > 0 && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">applied: {lastResult.applied}</Badge>}
            </div>
            {collisionCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 mt-1">
                <p className="text-amber-800 font-semibold flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> Route these via Collision-Pair Resolver:
                </p>
                <ul className="mt-1 space-y-1">
                  {lastResult.collisions.map((c) => (
                    <li key={c.rm_id} className="font-mono text-[11px]">
                      {c.rm_id} → would clash with {c.would_collide_with.join(", ")} on {c.pantone_code}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
